// DraftManager.jsx - Component for managing multiple drafts
import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  List, 
  Button, 
  Typography, 
  Space, 
  Tag, 
  Popconfirm, 
  Empty, 
  Input,
  Row,
  Col,
  Card,
  Avatar,
  Tooltip,
  message,
  Badge
} from 'antd';
import { 
  DeleteOutlined, 
  EditOutlined, 
  SaveOutlined, 
  HistoryOutlined,
  SearchOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { 
  getDraftsListAsync, 
  loadDraftAsync, 
  deleteDraftAsync, 
  clearAllDraftsAsync, 
  fetchDraftsAsync
} from '@/stores/slice/reportSlice/inspectionSlice';
import toast from "react-hot-toast";

const { Title, Text } = Typography;
const { Search } = Input;

const DraftManager = ({ 
  visible, 
  onClose, 
  onDraftSelected, 
  currentFormData ,reportType
}) => {
  const dispatch = useDispatch();
  const { draftsList, isDraftsListLoading } = useSelector(state => state.inspection);
      const { user } = useSelector(state => state.auth);

  const [searchText, setSearchText] = useState('');
  const [filteredDrafts, setFilteredDrafts] = useState([]);

  
      React.useEffect(() => {
        if(user)
      dispatch(fetchDraftsAsync({ user_id:  user.user_id, reportType }));
    }, [ user, reportType, dispatch]);

  useEffect(() => {
    if (visible) {
      dispatch(getDraftsListAsync());
    }
  }, [visible, dispatch]);

  useEffect(() => {
    const filtered = draftsList.filter(draft => 
      draft.title.toLowerCase().includes(searchText.toLowerCase()) ||
      draft.eventType.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredDrafts(filtered);
  }, [draftsList, searchText]);

  const handleLoadDraft = async (draftKey) => {
    try {
      const result = await dispatch(loadDraftAsync(draftKey,reportType)).unwrap();
      if (result) {
        toast.success('Draft loaded successfully!');
        onDraftSelected(result);
        onClose();
      }
    } catch (error) {
      toast.error('Failed to load draft: ' + error.message);
    }
  };

  const handleDeleteDraft = async (draftKey, title) => {
    try {
      await dispatch(deleteDraftAsync(draftKey)).unwrap();
      toast.success(`Draft "${title}" deleted successfully`);
    } catch (error) {
      toast.error('Failed to delete draft: ' + error.message);
    }
  };

  const handleClearAllDrafts = async () => {
    try {
      await dispatch(clearAllDraftsAsync()).unwrap();
      toast.success('All drafts cleared successfully');
    } catch (error) {
      toast.error('Failed to clear drafts: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      
      'DEFAULT': 'default'
    };
    return colors[eventType] || colors.DEFAULT;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HistoryOutlined />
          <span>Drafts</span>
          <Badge count={draftsList.length} style={{ backgroundColor: '#52c41a' }} />
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="clear" danger onClick={handleClearAllDrafts} disabled={draftsList.length === 0}>
          <ClearOutlined /> Clear All
        </Button>,
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>
      ]}
      bodyStyle={{ padding: '16px' }}
    >
      {/* Search and Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col span={16}>
          <Search
            placeholder="Search drafts by title or event type..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col span={8}>
          <div style={{ textAlign: 'right', color: '#666' }}>
            <Text type="secondary">
              {filteredDrafts.length} of {draftsList.length} drafts
            </Text>
          </div>
        </Col>
      </Row>

      {/* Drafts List */}
      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        {filteredDrafts.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              draftsList.length === 0 
                ? "No drafts saved yet" 
                : "No drafts match your search"
            }
          />
        ) : (
          <List
            dataSource={filteredDrafts}
            renderItem={(draft) => (
              <List.Item
                style={{
                  padding: '12px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  backgroundColor: '#fafafa'
                }}
                actions={[
                  <Tooltip title="Load Draft">
                    <Button
                      type="primary"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleLoadDraft(draft.draftKey)}
                    >
                      Load
                    </Button>
                  </Tooltip>,
                  <Popconfirm
                    title="Delete Draft"
                    description="Are you sure you want to delete this draft?"
                    onConfirm={() => handleDeleteDraft(draft.draftKey, draft.title)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      icon={<FileTextOutlined />} 
                      style={{ backgroundColor: getEventTypeColor(draft.eventType) }}
                    />
                  }
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text strong>{draft.title}</Text>
                      <Tag color={getEventTypeColor(draft.eventType)}>
                        {draft.eventType}
                      </Tag>
                    </div>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                        <span>
                          <CalendarOutlined style={{ marginRight: '4px' }} />
                          {formatDate(draft.date)}
                        </span>
                        
                        
                      </div>
                      
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
};

export default DraftManager;